import { onBeforeUnmount, onMounted, ref } from 'vue'

/**
 * Композабл для определения ручной прокрутки пользователя (touch-события).
 * 
 * Отличает реальные свайпы/жесты пользователя от программных скроллов и простых тапов.
 * Полезно для логики, которая должна реагировать только на намеренные действия пользователя.
 * 
 * @param activateThreshold - Минимальное расстояние движения (в px) для активации флага.
 *                            Значения меньше этого порога считаются случайными или кликами.
 * 
 * @returns isManualScrolling - Реактивный флаг, true когда пользователь активно скроллит свайпом
 * 
 * @example
 * ```ts
 * const { isManualScrolling } = useManualScroll(10)
 * 
 * // Использование в логике
 * if (isManualScrolling.value) {
 *   // Реагируем только на ручной скролл
 * }
 * ```
 */
export function useManualScroll(activateThreshold: number = 10) {
  /**
   * Флаг активного ручного скролла.
   * true - пользователь активно скроллит свайпом (touchmove с достаточным смещением)
   * false - нет активного скролла, или это программный скролл, или простой тап
   */
  const isManualScrolling = ref(false)

  /**
   * Начальная Y-координата касания.
   * Сохраняется при touchstart для вычисления дельты движения.
   */
  let touchStartY = 0

  /**
   * Обработчик начала касания (touchstart).
   * Сохраняет начальную позицию и сбрасывает флаг скролла.
   */
  const handleTouchStart = (e: TouchEvent) => {
    const touch = e.touches?.[0]
    if (touch) {
      touchStartY = touch.clientY
    }
    // На старте не считаем это скроллом — ждём движения
    isManualScrolling.value = false
  }

  /**
   * Обработчик движения пальца (touchmove).
   * Вычисляет дельту движения и активирует флаг если превышен порог.
   */
  const handleTouchMove = (e: TouchEvent) => {
    const touch = e.touches?.[0]
    if (touch) {
      const deltaY = Math.abs(touch.clientY - touchStartY)
      // Активируем флаг только если движение превысило порог
      if (deltaY > activateThreshold) {
        isManualScrolling.value = true
      }
    }
  }

  /**
   * Обработчик окончания касания (touchend, touchcancel).
   * Сбрасывает флаг скролла.
   */
  const handleTouchEnd = () => {
    isManualScrolling.value = false
  }

  /**
   * Подключаем обработчики при монтировании компонента.
   * Используем passive: true для лучшей производительности.
   */
  onMounted(() => {
    // Проверяем поддержку touch-событий
    if (typeof window !== 'undefined' && 'ontouchstart' in window) {
      window.addEventListener('touchstart', handleTouchStart, { passive: true })
      window.addEventListener('touchmove', handleTouchMove, { passive: true })
      window.addEventListener('touchend', handleTouchEnd, { passive: true })
      window.addEventListener('touchcancel', handleTouchEnd, { passive: true })
    }
  })

  /**
   * Отключаем обработчики при размонтировании компонента.
   */
  onBeforeUnmount(() => {
    if (typeof window !== 'undefined' && 'ontouchstart' in window) {
      window.removeEventListener('touchstart', handleTouchStart)
      window.removeEventListener('touchmove', handleTouchMove)
      window.removeEventListener('touchend', handleTouchEnd)
      window.removeEventListener('touchcancel', handleTouchEnd)
    }
  })

  return {
    /**
     * Реактивный флаг активного ручного скролла.
     * true - пользователь активно скроллит свайпом
     * false - нет активного скролла
     */
    isManualScrolling,
  }
}
